// app/features/industry-opportunities/components/OpportunityImpactSectionComponent.tsx
'use client';

import type { IndustryOpportunity } from '../industry-opportunity.interface';
import { getCategoryKey } from '../industry-opportunity.utils';

interface OpportunityImpactSectionComponentProps {
  opportunity: IndustryOpportunity;
}

// Border colors matching header colors
const CATEGORY_BORDER_COLORS: Record<string, string> = {
  'client-communication': 'border-blue',
  'client-updates': 'border-terracotta',
  'reporting': 'border-green',
  'operations': 'border-teal',
  'data-search': 'border-indigo',
  'onboarding': 'border-brown',
  'custom': 'border-stone-600',
};

export function OpportunityImpactSectionComponent({ 
  opportunity 
}: OpportunityImpactSectionComponentProps) {
  const hasBefore = opportunity.what_changes_before;
  const hasAfter = opportunity.what_changes_after;
  const hasMetric = opportunity.primary_metric;
  
  // Get transformation_shift from metadata
  const transformationShift = opportunity.metadata_json && 
    typeof opportunity.metadata_json === 'object' && 
    'transformation_shift' in opportunity.metadata_json
    ? String(opportunity.metadata_json.transformation_shift)
    : null;
  
  if (!hasBefore && !hasAfter && !hasMetric && !transformationShift) {
    return null;
  }

  const categoryKey = getCategoryKey(opportunity);
  const borderColor = CATEGORY_BORDER_COLORS[categoryKey] || 'border-stone-600';

  return (
    <div className="py-12 lg:py-16 bg-beige-light">
      <div className="container mx-auto px-4 md:px-6">
        <div className="max-w-4xl">
          <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 leading-tight mb-10">
            Expected Impact
          </h2>
          
          {/* Transformation Shift - The mindset change */}
          {transformationShift && (
            <p className="text-2xl text-gray-800 font-mono italic mb-10">
              {transformationShift}
            </p>
          )}
          
          {/* Before/After */}
          {(hasBefore || hasAfter) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10 mb-10">
              {hasBefore && (
                <div>
                  <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wider font-mono mb-3">
                    Before:
                  </h3>
                  <p className={`text-xl text-gray-600 leading-relaxed pl-5 border-l-4 ${borderColor}`}>
                    {opportunity.what_changes_before}
                  </p>
                </div>
              )}
              
              {hasAfter && (
                <div>
                  <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wider font-mono mb-3">
                    After:
                  </h3>
                  <p className={`text-xl text-gray-600 leading-relaxed pl-5 border-l-4 ${borderColor}`}>
                    {opportunity.what_changes_after}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Result */}
          {hasMetric && (
            <div className="pt-6 border-t border-gray-200">
              <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wider font-mono mb-3">
                Result:
              </h3>
              <p className="text-3xl font-bold text-black">
                {opportunity.primary_metric}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

